import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Form, Input, InputNumber, Select, Radio, message } from 'antd';
import { makePostCall, getUserDetails, setUserDetails } from '../../utils/helper';
import {
  getWorkstationLayout,
  setWorkstationLayout,
  DEFAULT_WORKSTATION_LAYOUT,
  type WorkstationLayout,
} from '../../utils/workstationLayout';
import {
  getReportPreviewChoice,
  setReportPreviewChoice,
  getReportPreviewDefault,
  canConfigureReportPreview,
  DEFAULT_REPORT_PREVIEW_CHOICE,
  MIN_PREVIEW_SECONDS,
  MAX_PREVIEW_SECONDS,
  type ReportPreviewChoice,
} from '../../utils/reportPreviewPref';
import { useSites } from '../../hooks/useSites';
import './edit-profile.css';

const { Option } = Select;

const EditProfile = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Workstation layout is a browser-local preference held outside the antd Form on
  // purpose: anything inside the Form gets diffed into the /add-user payload below, and
  // this setting has no column on the user record.
  const [layout, setLayout] = useState<WorkstationLayout>(DEFAULT_WORKSTATION_LAYOUT);
  // Post-commit report preview — same story as the layout above: a preference row, not a
  // user column, so it stays outside the antd Form.
  const [preview, setPreview] = useState<ReportPreviewChoice>(DEFAULT_REPORT_PREVIEW_CHOICE);
  // Only reporting roles ever reach the post-commit preview, so only they see the setting.
  const showPreviewSetting = canConfigureReportPreview(getUserDetails());
  const previewDefault = getReportPreviewDefault();
  const { centers } = useSites();

  // Fetch user data when modal opens
  useEffect(() => {
    if (visible) {
      loadUserData();
    } else {
      // Clean up when modal closes
      form.resetFields();
      setIsSubmitting(false);
    }
  }, [visible, form]);

  const loadUserData = () => {
    // Get user data from localStorage (same as what's already available)
    const userDetails = getUserDetails();
    if (!userDetails) {
      message.error('User information not found');
      return;
    }

    form.setFieldsValue({
      user_firstname: userDetails.user_firstname,
      user_lastname: userDetails.user_lastname,
      username: userDetails.username,
      user_email: userDetails.user_email,
      user_mobile: userDetails.user_mobile,
      user_unit_name: userDetails.user_unit_name,
      user_designation: userDetails.user_designation,
      user_degree: userDetails.user_degree,
    });

    setLayout(getWorkstationLayout());
    setPreview(getReportPreviewChoice());
  };

  const handleOk = useCallback(async () => {
    const userDetails = getUserDetails();
    if (!userDetails) {
      message.error('User data not loaded');
      return;
    }

    try {
      setIsSubmitting(true);

      // Validate all form fields first
      const values = await form.validateFields();

      // Compare current form values with original user data to detect changes
      const changedValues = {};

      // Check each form field against original data
      Object.keys(values).forEach(key => {
        const formValue = values[key];
        let originalValue = userDetails[key];

        // Detect actual changes (handle undefined/null/empty string cases)
        if (formValue !== originalValue && formValue !== '') {
          changedValues[key] = formValue;
        }
      });

      // Saved before the no-changes bail-out below, so flipping only a browser-local
      // preference still takes effect instead of being reported as "no changes".
      const layoutChanged = layout !== getWorkstationLayout();
      const layoutSaved = layoutChanged ? await setWorkstationLayout(layout) : true;
      if (layoutChanged && !layoutSaved) {
        message.warning(
          'Monitor layout applied on this browser, but it could not be saved to your profile'
        );
      }

      const storedPreview = getReportPreviewChoice();
      const previewChanged =
        showPreviewSetting &&
        (preview.mode !== storedPreview.mode || preview.seconds !== storedPreview.seconds);
      const previewSaved = previewChanged ? await setReportPreviewChoice(preview) : true;
      if (previewChanged && !previewSaved) {
        message.warning(
          'Report preview applied on this browser, but it could not be saved to your profile'
        );
      }

      const prefsChanged = layoutChanged || previewChanged;

      // Only submit if there are actual changes
      if (Object.keys(changedValues).length === 0) {
        if (prefsChanged) {
          if (layoutSaved && previewSaved) {
            message.success('Preferences saved');
          }
        } else {
          message.info('No changes detected');
        }
        onCancel();
        return;
      }

      // Sanitize input data (same as UserList component)
      const submitData = {
        ...changedValues,
        user_firstname: changedValues.user_firstname?.trim() || userDetails.user_firstname,
        user_lastname: changedValues.user_lastname?.trim() || userDetails.user_lastname,
        user_email: changedValues.user_email?.trim()?.toLowerCase() || userDetails.user_email,
        user_mobile: changedValues.user_mobile?.trim() || userDetails.user_mobile,
        user_designation: changedValues.user_designation?.trim() || userDetails.user_designation,
        user_degree: changedValues.user_degree?.trim() || userDetails.user_degree,
        existing_user_id: userDetails.user_id,
        user_id: getUserDetails().username, // This is required by the backend
      };

      // Submit to backend using the same endpoint as EditUser
      const response = await makePostCall('/add-user', submitData);

      if (response.data?.success) {
        message.success('Profile updated successfully');

        // Fetch fresh user data from backend to sync localStorage
        try {
          const userListResponse = await makePostCall('/user-list', {});
          const allUsers = userListResponse.data?.data || [];

          // Find the current user in the list
          const updatedUser = allUsers.find(u => u.user_id === userDetails.user_id);

          if (updatedUser) {
            // Prepare user details object matching the expected format
            const freshUserDetails = {
              user_id: updatedUser.user_id,
              username: updatedUser.username,
              user_firstname: updatedUser.user_firstname || updatedUser.first_name,
              user_lastname: updatedUser.user_lastname || updatedUser.last_name,
              user_fullname: updatedUser.user_fullname || `${updatedUser.user_firstname || updatedUser.first_name} ${updatedUser.user_lastname || updatedUser.last_name || ''}`,
              user_email: updatedUser.user_email,
              user_mobile: updatedUser.user_mobile,
              user_type: updatedUser.user_type,
              user_unit_name: updatedUser.user_unit_name,
              user_designation: updatedUser.user_designation,
              user_degree: updatedUser.user_degree,
              user_signature: updatedUser.user_signature,
              // hasPermission() reads `permissions` (the key login stores), so
              // the refreshed object must carry it — writing only
              // `user_permissions` here used to blank out every permission-gated
              // control for the rest of the session.
              permissions: updatedUser.user_permissions || userDetails?.permissions || [],
              user_permissions: updatedUser.user_permissions || []
            };

            // Update localStorage using the helper function
            setUserDetails(freshUserDetails);
          }
        } catch (error) {
          console.error('Error fetching updated user data:', error);
          // Still close the modal even if refresh fails
        }

        onCancel();

        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error(response.data?.message || 'Failed to update profile');
      }
    } catch (errorInfo) {
      if (errorInfo.errorFields) {
        message.error('Please fix the validation errors in the form');
      } else {
        console.error('Update failed:', errorInfo);
        message.error('Failed to update profile. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [form, layout, preview, showPreviewSetting, onCancel, onSuccess]);

  const unitOptions = useMemo(() => centers, [centers]);

  return (
    <Modal
      open={visible}
      title="Edit Profile"
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
      width={700}
      className="edit-profile-modal"
      confirmLoading={isSubmitting}
      okText={isSubmitting ? "Saving..." : "Save Changes"}
      cancelButtonProps={{ disabled: isSubmitting }}
    >
      <Form
        layout="vertical"
        form={form}
        className="edit-profile-modal__form"
      >
        {/* Personal Information */}
        <div className="form-section-title">Personal Information</div>
        <div className="edit-profile-modal__form-row">
          <Form.Item
            className="edit-profile-modal__form-item"
            label="First Name"
            name="user_firstname"
            rules={[
              {
                pattern: /^[a-zA-Z0-9\s.]+$/,
                message: "First name can only contain letters, numbers, spaces, and periods!",
              },
            ]}
          >
            <Input placeholder="Enter first name" disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Last Name"
            name="user_lastname"
            rules={[
              {
                pattern: /^[a-zA-Z0-9\s.]*$/,
                message: "Last name can only contain letters, numbers, spaces, and periods!",
              },
            ]}
          >
            <Input placeholder="Enter last name" disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Username"
            name="username"
            rules={[
              {
                pattern: /^[a-zA-Z0-9_.]+$/,
                message: "Username can only contain letters, numbers, underscores, and dots!",
              },
            ]}
          >
            <Input placeholder="Enter username" disabled />
          </Form.Item>
        </div>

        {/* Contact Information */}
        <div className="form-section-title">Contact Information</div>
        <div className="edit-profile-modal__form-row">
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Email"
            name="user_email"
            rules={[
              {
                type: "email",
                message: "Please enter a valid email address!",
              },
            ]}
          >
            <Input type="email" placeholder="Enter email address" disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Mobile"
            name="user_mobile"
            rules={[
              {
                pattern: /^[6-9]\d{9}$/,
                message: "Please enter a valid 10-digit mobile number starting with 6-9!",
              },
            ]}
          >
            <Input placeholder="Enter mobile number" maxLength={10} />
          </Form.Item>
        </div>

        {/* Professional Details */}
        <div className="form-section-title">Professional Details</div>
        <div className="edit-profile-modal__form-row">
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Location"
            name="user_unit_name"
          >
            <Select placeholder="Select Unit" options={unitOptions} disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Designation"
            name="user_designation"
          >
            <Input placeholder="Enter designation" />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Degree"
            name="user_degree"
          >
            <Input placeholder="Enter degree" />
          </Form.Item>
        </div>

        {/* Workstation Layout — stored in this browser for your login, not on the server */}
        <div className="form-section-title">Workstation Layout</div>
        <div className="edit-profile-modal__layout">
          <Radio.Group
            className="edit-profile-modal__layout-options"
            value={layout}
            onChange={e => setLayout(e.target.value)}
            disabled={isSubmitting}
          >
            <Radio value="viewer-other">
              Keep reporting on this monitor, open images on the other
            </Radio>
            <Radio value="reporting-other">
              Keep images on this monitor, open reporting on the other
            </Radio>
          </Radio.Group>
          <div className="edit-profile-modal__layout-hint">
            Applies on a dual-monitor desk — “this monitor” means whichever one you have PACS
            open on. Saved to your profile, so it follows you to any computer you log in
            from.
          </div>
        </div>

        {/* Report Preview — how long the committed report is held on screen after Draft /
            Reviewed / Sign off, before the window closes or the queue advances. */}
        {showPreviewSetting && (
          <>
            <div className="form-section-title">Report Preview After Saving</div>
            <div className="edit-profile-modal__layout">
              <Radio.Group
                className="edit-profile-modal__layout-options"
                value={preview.mode}
                onChange={e => setPreview(prev => ({ ...prev, mode: e.target.value }))}
                disabled={isSubmitting}
              >
                <Radio value="default">
                  Use the default for this centre (
                  {previewDefault.enabled
                    ? `show for ${previewDefault.seconds} second${previewDefault.seconds === 1 ? '' : 's'}`
                    : 'do not show'}
                  )
                </Radio>
                <Radio value="off">
                  Don’t show a preview — close or move to the next study straight away
                </Radio>
                <Radio value="on">
                  <span className="edit-profile-modal__preview-on">
                    Show the saved report for
                    <InputNumber
                      className="edit-profile-modal__preview-seconds"
                      min={MIN_PREVIEW_SECONDS}
                      max={MAX_PREVIEW_SECONDS}
                      precision={0}
                      value={preview.seconds}
                      // Selecting a duration is the same statement as choosing this option,
                      // so typing here also picks the radio — otherwise a user sets 10
                      // seconds, leaves the radio on "default", and nothing happens.
                      onChange={value =>
                        setPreview(prev => ({
                          ...prev,
                          mode: 'on',
                          seconds: Number(value) || prev.seconds,
                        }))
                      }
                      onClick={e => e.stopPropagation()}
                      disabled={isSubmitting}
                    />
                    seconds
                  </span>
                </Radio>
              </Radio.Group>
              <div className="edit-profile-modal__layout-hint">
                After you click Draft, Reviewed or Sign off, the finished report can be held
                on screen read-only for a few seconds so you can check it. Saved to your
                profile, so it follows you to any computer you log in from.
              </div>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditProfile;
